<template>
  <Dialog
    :visible.sync="visibleProxy"
    :containerStyle="{ width: $isMobile() ? '100vw' : '82vw' }"
    :class="{ 'p-dialog-maximized': $isMobile() }"
    :maximizable="true"
    :modal="true"
    header="Importar imagens em lote"
    @show="onShow"
    @hide="onHide"
  >
    <div class="bulk-image-manager">
      <div class="mode-tabs" role="tablist" aria-label="Tipo de imagens">
        <button
          v-for="option in modes"
          :key="option.value"
          type="button"
          class="mode-tab"
          :class="{ active: mode === option.value }"
          :aria-selected="mode === option.value"
          role="tab"
          @click="changeMode(option.value)"
        >
          <i :class="option.icon"></i>
          <span>{{ option.label }}</span>
          <small v-if="imports[option.value].id">#{{ imports[option.value].id }}</small>
        </button>
      </div>

      <section class="guidance-card">
        <div>
          <span class="section-kicker">Convenção de nomes</span>
          <h3>{{ currentMode.label }}</h3>
          <p v-if="mode === 'equipment'">
            Use a série do equipamento: <code>OLED55C1.jpg</code>, <code>OLED55C1_02.jpg</code>.
          </p>
          <p v-else>
            Use a referência do produto: <code>AGF78400702.jpg</code>, <code>AGF78400702_02.jpg</code>.
          </p>
          <p class="guidance-detail">
            A imagem sem sufixo fica primeiro; apenas <code>_NN</code> define a ordem da galeria.
            A galeria submetida substitui integralmente a atual.
          </p>
        </div>
        <div class="limits">
          <span><strong>100</strong> imagens no máximo</span>
          <span><strong>4 MB</strong> por imagem</span>
          <span><strong>10</strong> imagens por bloco</span>
        </div>
      </section>

      <section v-if="!currentImport.id" class="upload-card">
        <div class="upload-summary">
          <span>{{ selectedFiles.length }} / {{ maxFiles }} imagens selecionadas</span>
          <span>{{ formatBytes(selectedBytes) }}</span>
          <Button
            label="Iniciar importação"
            icon="pi pi-cloud-upload"
            class="p-button-success"
            :disabled="!selectedFiles.length || uploading"
            :loading="uploading"
            @click="startImport"
          />
        </div>
        <AppFileUpload
          ref="imageUpload"
          mode="advanced"
          name="files[]"
          chooseLabel="Escolher imagens"
          accept="image/jpeg,image/png,image/gif,image/webp"
          invalidFileTypeMessage="{0}: formato inválido. Use JPEG, PNG, GIF ou WebP."
          invalidFileSizeMessage="{0}: excede o limite de {1}."
          invalidFileLimitMessage="Só pode selecionar até {0} imagens."
          :maxFileSize="maxFileSize"
          :fileLimit="maxFiles"
          :multiple="true"
          :showUploadButton="false"
          :showCancelButton="true"
          :previewWidth="72"
          @select="syncFiles"
          @remove="syncFiles"
          @clear="selectedFiles = []"
        />
      </section>

      <section v-else class="status-card">
        <div class="status-heading">
          <div>
            <span class="section-kicker">Importação #{{ currentImport.id }}</span>
            <h3>{{ phaseLabel }}</h3>
            <p>{{ phaseDescription }}</p>
          </div>
          <span class="status-pill" :class="statusTone">{{ statusLabel }}</span>
        </div>

        <div class="phase-track">
          <div
            v-for="(phase, index) in phases"
            :key="phase.value"
            class="phase-step"
            :class="{ complete: index < phaseIndex, current: index === phaseIndex }"
          >
            <span>{{ index + 1 }}</span>
            <small>{{ phase.label }}</small>
          </div>
        </div>

        <div class="progress-panel" v-if="uploading || currentStatus.uploaded_count < currentStatus.expected_count">
          <div class="progress-copy">
            <span>Envio em blocos de 10</span>
            <strong>{{ uploadProgress }}%</strong>
          </div>
          <div class="progress-rail"><span :style="{ width: uploadProgress + '%' }"></span></div>
        </div>

        <div class="count-grid">
          <div v-for="count in counts" :key="count.key" class="count-card" :class="count.tone">
            <span>{{ count.label }}</span>
            <strong>{{ count.value }}</strong>
          </div>
        </div>

        <div v-if="importError" class="import-error">
          <i class="pi pi-exclamation-triangle"></i>
          {{ importError }}
        </div>

        <div class="status-actions">
          <Button
            label="Atualizar"
            icon="pi pi-refresh"
            class="p-button-secondary p-button-sm"
            :loading="statusLoading"
            @click="loadStatus(mode, true)"
          />
          <Button
            v-if="isCurrentActive"
            label="Cancelar importação"
            icon="pi pi-ban"
            class="p-button-danger p-button-sm"
            :disabled="uploading || actionLoading"
            @click="confirmCancelImport"
          />
          <Button
            v-if="canStartAnother"
            label="Nova importação"
            icon="pi pi-plus"
            class="p-button-outlined p-button-sm"
            @click="clearCurrentImport"
          />
        </div>
      </section>

      <section v-if="currentImport.id" class="review-card">
        <div class="review-heading">
          <div>
            <span class="section-kicker">Caixa de revisão</span>
            <h3>Imagens que precisam de atenção</h3>
            <p>Corrija a chave, tente novamente ou elimine a imagem da importação.</p>
          </div>
          <Button
            icon="pi pi-refresh"
            class="p-button-rounded p-button-text"
            :loading="itemsLoading"
            v-tooltip="'Atualizar caixa de revisão'"
            @click="loadItems(1)"
          />
        </div>

        <div v-if="itemsLoading && !items.length" class="review-empty">A carregar imagens...</div>
        <div v-else-if="!items.length" class="review-empty">
          <i class="pi pi-check-circle"></i>
          Nenhuma imagem pendente nesta página.
        </div>
        <div v-else class="review-list">
          <article v-for="item in items" :key="item.id" class="review-item">
            <div class="item-preview">
              <AuthenticatedImage
                v-if="item.preview_url"
                :file="item.preview_url"
                :alt="itemFilename(item)"
                fileServiceMethod="getSparsImportPreview"
              />
              <i v-else class="pi pi-image"></i>
            </div>
            <div class="item-main">
              <div class="item-title">
                <strong :title="itemFilename(item)">{{ itemFilename(item) }}</strong>
                <span class="reason-pill" :class="reasonTone(item)">{{ reasonLabel(item) }}</span>
              </div>
              <small v-if="item.last_error">{{ item.last_error }}</small>
              <div class="key-editor">
                <span class="p-float-label">
                  <InputText
                    :id="`matching-key-${item.id}`"
                    v-model="editingKeys[item.id]"
                    :disabled="isItemBusy(item.id) || !can('createUpdateParts')"
                    @keyup.enter="renameItem(item)"
                  />
                  <label :for="`matching-key-${item.id}`">{{ currentMode.keyLabel }}</label>
                </span>
              </div>
            </div>
            <div class="item-actions" v-if="can('createUpdateParts')">
              <Button
                icon="pi pi-save"
                class="p-button-success p-button-sm"
                v-tooltip="'Guardar chave'"
                :disabled="!hasKeyChanged(item) || isItemBusy(item.id)"
                @click="renameItem(item)"
              />
              <Button
                icon="pi pi-refresh"
                class="p-button-info p-button-sm"
                v-tooltip="'Tentar novamente'"
                :disabled="isItemBusy(item.id)"
                @click="retryItem(item)"
              />
              <Button
                icon="pi pi-trash"
                class="p-button-danger p-button-sm"
                v-tooltip="'Eliminar da importação'"
                :disabled="isItemBusy(item.id)"
                @click="confirmDeleteItem(item)"
              />
            </div>
          </article>
        </div>

        <Paginator
          v-if="itemsTotal > itemsPerPage"
          :first="(itemsPage - 1) * itemsPerPage"
          :rows="itemsPerPage"
          :totalRecords="itemsTotal"
          template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
          currentPageReportTemplate="{first} a {last} de {totalRecords}"
          @page="loadItems($event.page + 1)"
        />
      </section>
    </div>

    <template #footer>
      <Button label="Fechar" icon="pi pi-times" class="p-button-secondary" @click="visibleProxy = false" />
    </template>
  </Dialog>
</template>

<script>
import AppFileUpload from "@/components/elements/AppFileUpload.vue";
import AuthenticatedImage from "@/components/elements/AuthenticatedImage.vue";
import sparsService from "@/services/spars.service.js";

const EMPTY_IMPORT = () => ({ id: null, status: {}, timer: null, inFlight: false });
const STORAGE_KEYS = {
  equipment: "spars.imageImport.equipment.id",
  product: "spars.imageImport.product.id",
};

export default {
  name: "BulkImageManager",
  components: { AppFileUpload, AuthenticatedImage },
  props: {
    visible: { type: Boolean, required: true },
  },
  data() {
    return {
      mode: "equipment",
      modes: [
        { value: "equipment", label: "Equipamentos", keyLabel: "Série do equipamento", icon: "pi pi-desktop" },
        { value: "product", label: "Produtos", keyLabel: "Referência do produto", icon: "pi pi-box" },
      ],
      imports: {
        equipment: EMPTY_IMPORT(),
        product: EMPTY_IMPORT(),
      },
      selectedFiles: [],
      uploading: false,
      uploadProgress: 0,
      statusLoading: false,
      itemsLoading: false,
      actionLoading: false,
      itemActions: {},
      items: [],
      editingKeys: {},
      itemsPage: 1,
      itemsPerPage: 10,
      itemsTotal: 0,
      destroyed: false,
      maxFiles: 100,
      maxFileSize: 4 * 1024 * 1024,
      phases: [
        { value: "upload", label: "Envio" },
        { value: "matching", label: "Em análise" },
        { value: "local_staging", label: "Galeria local" },
        { value: "prestashop_sync", label: "Loja" },
        { value: "completed", label: "Concluído" },
      ],
    };
  },
  computed: {
    visibleProxy: {
      get() { return this.visible; },
      set(value) { this.$emit("update:visible", value); },
    },
    currentMode() {
      return this.modes.find(item => item.value === this.mode);
    },
    currentImport() {
      return this.imports[this.mode];
    },
    currentStatus() {
      return this.currentImport.status || {};
    },
    importError() {
      const error = this.currentStatus.last_error || this.currentStatus.error || this.currentStatus.errors;
      return Array.isArray(error) ? error.join("; ") : error;
    },
    selectedBytes() {
      return this.selectedFiles.reduce((total, file) => total + file.size, 0);
    },
    normalizedPhase() {
      const rawPhase = String(this.currentStatus.phase || this.currentStatus.status || "uploading").toLowerCase();
      if (["created", "uploading", "pending_upload", "draft"].includes(rawPhase)) return "upload";
      if (["queued", "processing", "matching", "match"].includes(rawPhase)) return "matching";
      if (["staging", "local", "local_staging", "gallery_staging"].includes(rawPhase)) return "local_staging";
      if (["syncing", "remote_sync", "prestashop", "prestashop_sync", "remote_pending"].includes(rawPhase)) return "prestashop_sync";
      if (["completed", "completed_with_errors", "completed_with_pending", "complete", "finished", "failed", "queue_failed", "cancelled", "canceled"].includes(rawPhase)) return "completed";
      return "matching";
    },
    phaseIndex() {
      return Math.max(0, this.phases.findIndex(phase => phase.value === this.normalizedPhase));
    },
    phaseLabel() {
      const labels = {
        upload: "A receber imagens",
        matching: "A associar nomes",
        local_staging: "A preparar imagens locais",
        prestashop_sync: "A sincronizar com a loja",
        completed: this.isCancelled ? "Importação cancelada" : "Processamento terminado",
      };
      return labels[this.normalizedPhase];
    },
    phaseDescription() {
      if (this.uploading) return "Não feche esta janela até todos os blocos serem enviados.";
      if (this.normalizedPhase === "upload") return "A sessão está criada e aguarda o envio ou a finalização.";
      if (this.normalizedPhase === "matching") return "Os nomes estão a ser comparados com os registos existentes.";
      if (this.normalizedPhase === "local_staging") return "As imagens ordenadas estão a ser preparadas.";
      if (this.normalizedPhase === "prestashop_sync") return "As imagens locais estão a ser reconciliadas com o PrestaShop.";
      return "Consulte os totais e resolva as imagens na caixa de revisão.";
    },
    statusValue() {
      return String(this.currentStatus.status || this.currentStatus.phase || "uploading").toLowerCase();
    },
    isCancelled() {
      return ["cancelled", "canceled"].includes(this.statusValue) || Boolean(this.currentStatus.cancelled_at);
    },
    isCurrentActive() {
      return Boolean(this.currentImport.id) && !this.isTerminal(this.currentStatus);
    },
    canStartAnother() {
      return Boolean(this.currentImport.id) && this.isTerminal(this.currentStatus) && !this.uploading;
    },
    statusLabel() {
      if (this.isCancelled) return "Cancelada";
      if (["failed", "error", "queue_failed", "completed_with_errors"].includes(this.statusValue)) return "Com falhas";
      if (this.statusValue === "completed_with_pending") return "Revisão pendente";
      if (this.isTerminal(this.currentStatus)) return "Concluída";
      return "Em curso";
    },
    statusTone() {
      if (this.isCancelled || ["failed", "error", "queue_failed", "completed_with_errors"].includes(this.statusValue)) return "danger";
      if (this.statusValue === "completed_with_pending") return "active";
      if (this.isTerminal(this.currentStatus)) return "success";
      return "active";
    },
    counts() {
      const status = this.currentStatus;
      const value = (...keys) => {
        const key = keys.find(candidate => status[candidate] !== undefined && status[candidate] !== null);
        return key ? status[key] : 0;
      };
      return [
        { key: "uploaded", label: "Recebidas", value: value("uploaded_count", "uploaded"), tone: "neutral" },
        { key: "processed", label: "Processadas", value: value("processed_count", "processed"), tone: "neutral" },
        { key: "matched", label: "Associadas", value: value("matched_count", "matched"), tone: "positive" },
        { key: "unmatched", label: "Sem correspondência", value: value("unmatched_count", "unmatched"), tone: "warning" },
        { key: "pending", label: "Pendentes", value: value("pending_count", "pending"), tone: "warning" },
        { key: "succeeded", label: "Concluídas", value: value("succeeded_count", "succeeded"), tone: "positive" },
        { key: "failed", label: "Falharam", value: value("failed_count", "failed"), tone: "danger" },
      ];
    },
  },
  created() {
    this.restoreImports();
  },
  deactivated() {
    this.visibleProxy = false;
    document.body.classList.remove('p-overflow-hidden');
  },
  beforeDestroy() {
    this.destroyed = true;
    Object.keys(this.imports).forEach(mode => this.stopPolling(mode));
    document.body.classList.remove('p-overflow-hidden');
  },
  methods: {
    onShow() {
      if (this.currentImport.id) {
        this.loadStatus(this.mode, true);
        this.loadItems(1);
      }
    },
    onHide() {
      this.selectedFiles = [];
      if (this.$refs.imageUpload) this.$refs.imageUpload.clear();
    },
    changeMode(mode) {
      if (this.uploading) return;
      this.mode = mode;
      this.items = [];
      this.editingKeys = {};
      this.itemsPage = 1;
      this.itemsTotal = 0;
      this.selectedFiles = [];
      this.$nextTick(() => {
        if (this.$refs.imageUpload) this.$refs.imageUpload.clear();
      });
      if (this.currentImport.id) {
        this.loadStatus(mode, true);
        this.loadItems(1);
      }
    },
    syncFiles(event) {
      this.selectedFiles = event && event.files ? [...event.files] : [];
    },
    async startImport() {
      if (!this.selectedFiles.length || this.uploading) return;
      const files = [...this.selectedFiles];
      const importMode = this.mode;
      this.uploading = true;
      this.uploadProgress = 0;
      try {
        const created = this.unwrapResource(await sparsService.createImageImport(importMode, files.length));
        const importId = created.id || created.import_id;
        if (!importId) throw new Error("A API não devolveu o identificador da importação.");
        this.imports[importMode].id = importId;
        this.imports[importMode].status = created;
        this.persistImport(importMode);

        for (let offset = 0; offset < files.length; offset += 10) {
          const chunk = files.slice(offset, offset + 10);
          const chunkIndex = Math.floor(offset / 10);
          await sparsService.uploadImageImportChunk(
            importId,
            chunk,
            `chunk-${chunkIndex}`,
            offset,
            event => {
              if (!event.total) return;
              const chunkProgress = event.loaded / event.total;
              this.uploadProgress = Math.min(99, Math.round(((offset + chunk.length * chunkProgress) / files.length) * 100));
            }
          );
        }

        this.uploadProgress = 100;
        const finalized = this.unwrapResource(await sparsService.finalizeImageImport(importId));
        if (finalized && Object.keys(finalized).length) this.imports[importMode].status = finalized;
        this.$toast.add({
          severity: "success",
          summary: "Imagens recebidas",
          detail: `${files.length} imagem(ns) enviadas. O processamento continua em segundo plano.`,
          life: 5000,
        });
        if (this.$refs.imageUpload) this.$refs.imageUpload.clear();
        this.selectedFiles = [];
        await this.loadStatus(importMode, true);
        if (this.mode === importMode) await this.loadItems(1);
      } catch (error) {
        this.showError(error, "Não foi possível enviar a importação. A sessão foi mantida para consulta ou cancelamento.");
        if (this.imports[importMode].id) this.schedulePoll(importMode, 5000);
      } finally {
        this.uploading = false;
      }
    },
    restoreImports() {
      Object.keys(STORAGE_KEYS).forEach(mode => {
        try {
          const id = localStorage.getItem(STORAGE_KEYS[mode]);
          if (id) {
            this.imports[mode].id = id;
            this.loadStatus(mode);
          }
        } catch (error) {
          console.error(error);
        }
      });
    },
    persistImport(mode) {
      try {
        localStorage.setItem(STORAGE_KEYS[mode], this.imports[mode].id);
      } catch (error) {
        console.error(error);
      }
    },
    async loadStatus(mode = this.mode, notify = false) {
      const state = this.imports[mode];
      if (!state.id || state.inFlight || this.destroyed) return;
      state.inFlight = true;
      if (mode === this.mode) this.statusLoading = true;
      try {
        const status = this.unwrapResource(await sparsService.getImageImport(state.id));
        if (this.destroyed) return;
        state.status = status || {};
        if (this.isTerminal(state.status)) {
          this.stopPolling(mode);
        } else {
          this.schedulePoll(mode);
        }
        if (mode === this.mode && this.visible && this.shouldRefreshItems(state.status)) {
          this.loadItems(this.itemsPage);
        }
        if (notify) {
          this.$toast.add({ severity: "info", summary: "Estado atualizado", detail: `Importação #${state.id}`, life: 1800 });
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.removeStoredImport(mode);
          state.id = null;
          state.status = {};
          return;
        }
        this.schedulePoll(mode, 6000);
        if (notify) this.showError(error, "Não foi possível atualizar o estado da importação.");
      } finally {
        state.inFlight = false;
        if (mode === this.mode) this.statusLoading = false;
      }
    },
    schedulePoll(mode, delay = 3000) {
      this.stopPolling(mode);
      if (!this.destroyed && this.imports[mode].id && !this.isTerminal(this.imports[mode].status)) {
        this.imports[mode].timer = setTimeout(() => this.loadStatus(mode), delay);
      }
    },
    stopPolling(mode) {
      if (this.imports[mode].timer) {
        clearTimeout(this.imports[mode].timer);
        this.imports[mode].timer = null;
      }
    },
    async loadItems(page = 1) {
      if (!this.currentImport.id || this.itemsLoading) return;
      const importId = this.currentImport.id;
      this.itemsLoading = true;
      try {
        const response = await sparsService.getImageImportItems(importId, {
          page,
          per_page: this.itemsPerPage,
        });
        if (importId !== this.currentImport.id) return;
        const paginator = this.unwrapPaginator(response);
        this.items = paginator.items;
        this.itemsPage = paginator.currentPage;
        this.itemsTotal = paginator.total;
        const keys = {};
        this.items.forEach(item => { keys[item.id] = item.matching_key || ""; });
        this.editingKeys = keys;
      } catch (error) {
        this.showError(error, "Não foi possível carregar a caixa de revisão.");
      } finally {
        this.itemsLoading = false;
      }
    },
    confirmCancelImport() {
      this.$confirm.require({
        header: "Cancelar importação de imagens",
        message: "Pretende cancelar o trabalho que ainda não foi processado? As tarefas já iniciadas podem terminar.",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Sim, cancelar",
        acceptClass: "p-button-danger p-button p-component",
        rejectLabel: "Manter",
        rejectClass: "p-button-secondary p-button p-component",
        accept: () => this.cancelImport(),
      });
    },
    async cancelImport() {
      this.actionLoading = true;
      try {
        const response = this.unwrapResource(await sparsService.cancelImageImport(this.currentImport.id));
        if (response && Object.keys(response).length) this.currentImport.status = response;
        await this.loadStatus(this.mode);
        this.$toast.add({ severity: "info", summary: "Cancelamento solicitado", detail: "A importação será interrompida em segurança.", life: 4000 });
      } catch (error) {
        this.showError(error, "Não foi possível cancelar a importação.");
      } finally {
        this.actionLoading = false;
      }
    },
    clearCurrentImport() {
      if (!this.canStartAnother) return;
      this.stopPolling(this.mode);
      this.removeStoredImport(this.mode);
      this.imports[this.mode].id = null;
      this.imports[this.mode].status = {};
      this.items = [];
      this.editingKeys = {};
      this.itemsTotal = 0;
      this.uploadProgress = 0;
    },
    async renameItem(item) {
      const matchingKey = (this.editingKeys[item.id] || "").trim();
      if (!matchingKey || !this.hasKeyChanged(item) || this.isItemBusy(item.id)) return;
      this.$set(this.itemActions, item.id, true);
      try {
        await sparsService.renameImageImportItem(this.currentImport.id, item.id, matchingKey);
        this.$set(item, "matching_key", matchingKey);
        this.$toast.add({ severity: "success", summary: "Chave atualizada", detail: matchingKey, life: 2200 });
        await this.loadStatus(this.mode);
      } catch (error) {
        this.showError(error, "Não foi possível alterar a chave de correspondência.");
      } finally {
        this.$delete(this.itemActions, item.id);
      }
    },
    async retryItem(item) {
      if (this.isItemBusy(item.id)) return;
      this.$set(this.itemActions, item.id, true);
      try {
        const matchingKey = (this.editingKeys[item.id] || "").trim();
        if (matchingKey && matchingKey !== (item.matching_key || "")) {
          await sparsService.renameImageImportItem(this.currentImport.id, item.id, matchingKey);
        } else {
          await sparsService.retryImageImportItem(this.currentImport.id, item.id);
        }
        this.$toast.add({ severity: "info", summary: "Nova tentativa em fila", detail: this.itemFilename(item), life: 3000 });
        await this.refreshAfterItemAction();
      } catch (error) {
        this.showError(error, "Não foi possível repetir o processamento da imagem.");
      } finally {
        this.$delete(this.itemActions, item.id);
      }
    },
    confirmDeleteItem(item) {
      this.$confirm.require({
        header: "Eliminar imagem",
        message: `Pretende eliminar “${this.itemFilename(item)}” desta importação?`,
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Eliminar",
        acceptClass: "p-button-danger p-button p-component",
        rejectLabel: "Manter",
        rejectClass: "p-button-secondary p-button p-component",
        accept: () => this.deleteItem(item),
      });
    },
    async deleteItem(item) {
      this.$set(this.itemActions, item.id, true);
      try {
        await sparsService.deleteImageImportItem(this.currentImport.id, item.id);
        this.$toast.add({ severity: "success", summary: "Imagem eliminada", detail: this.itemFilename(item), life: 2500 });
        await this.refreshAfterItemAction();
      } catch (error) {
        this.showError(error, "Não foi possível eliminar a imagem.");
      } finally {
        this.$delete(this.itemActions, item.id);
      }
    },
    async refreshAfterItemAction() {
      await this.loadStatus(this.mode);
      const targetPage = this.items.length === 1 && this.itemsPage > 1 ? this.itemsPage - 1 : this.itemsPage;
      await this.loadItems(targetPage);
    },
    isItemBusy(itemId) {
      return Boolean(this.itemActions[itemId]);
    },
    hasKeyChanged(item) {
      return Boolean((this.editingKeys[item.id] || "").trim()) && (this.editingKeys[item.id] || "").trim() !== (item.matching_key || "");
    },
    itemFilename(item) {
      return item.original_filename || item.file_name || item.staged_filename || `Imagem #${item.id}`;
    },
    reasonLabel(item) {
      const reason = String(item.reason || item.status || "pending").toLowerCase();
      const labels = {
        unmatched: "Sem correspondência",
        not_published: "Não publicado",
        missing_api_id: "Não publicado",
        remote_pending: "Sincronização pendente",
        pending_remote: "Sincronização pendente",
        integration_disabled: "Sincronização pendente",
        remote_failed: "Falha na loja",
        failed_remote_sync: "Falha na loja",
        failed: "Falha",
        invalid_filename: "Nome inválido",
        unsupported_content: "Conteúdo inválido",
        superseded: "Substituída por duplicado",
        pending: "Pendente",
      };
      return labels[reason] || reason.replace(/_/g, " ");
    },
    reasonTone(item) {
      const reason = String(item.reason || item.status || "").toLowerCase();
      if (reason.includes("fail") || reason.includes("invalid") || reason.includes("unsupported")) return "danger";
      if (reason.includes("pending") || reason.includes("published") || reason.includes("api_id")) return "warning";
      return "neutral";
    },
    shouldRefreshItems(status) {
      const unresolved = Number(status.unmatched_count || 0) + Number(status.pending_count || 0) + Number(status.failed_count || 0);
      return unresolved > 0 || this.isTerminal(status);
    },
    isTerminal(status) {
      const value = String(status.status || status.phase || "").toLowerCase();
      return ["completed", "completed_with_errors", "completed_with_pending", "complete", "finished", "failed", "error", "queue_failed", "cancelled", "canceled"].includes(value)
        || Boolean(status.completed_at || status.cancelled_at || status.finished_at);
    },
    unwrapResource(response) {
      if (response && response.import) return response.import;
      if (response && response.item) return response.item;
      if (response && response.data && !Array.isArray(response.data) && response.id === undefined && response.import_id === undefined) {
        return response.data.import || response.data.item || response.data;
      }
      return response || {};
    },
    unwrapPaginator(response) {
      const envelope = response || {};
      const source = envelope.items && !Array.isArray(envelope.items)
        ? envelope.items
        : (envelope.data && !Array.isArray(envelope.data) && Array.isArray(envelope.data.data)
          ? envelope.data
          : envelope);
      const items = Array.isArray(source) ? source : (Array.isArray(source.data) ? source.data : (source.items || []));
      return {
        items,
        currentPage: Number(source.current_page || source.page || 1),
        total: Number(source.total !== undefined ? source.total : items.length),
      };
    },
    removeStoredImport(mode) {
      try {
        localStorage.removeItem(STORAGE_KEYS[mode]);
      } catch (error) {
        console.error(error);
      }
    },
    showError(error, fallback) {
      const detail = error && error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : (error && error.message ? error.message : fallback);
      this.$toast.add({ severity: "error", summary: "Erro", detail, life: 5000 });
    },
    formatBytes(bytes) {
      if (!bytes) return "0 MB";
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    },
  },
};
</script>

<style scoped>
.bulk-image-manager {
  color: #263238;
  display: grid;
  gap: 1rem;
}

.mode-tabs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
  padding: 0.35rem;
  background: #eef2f4;
  border-radius: 12px;
}

.mode-tab {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  min-height: 48px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #607078;
  cursor: pointer;
  font: inherit;
  font-weight: 600;
}

.mode-tab.active {
  color: #123b4a;
  background: #fff;
  box-shadow: 0 2px 8px rgba(18, 59, 74, 0.12);
}

.mode-tab small { color: #829199; font-weight: 400; }

.guidance-card,
.upload-card,
.status-card,
.review-card {
  border: 1px solid #dce4e7;
  border-radius: 14px;
  background: #fff;
  padding: 1.1rem;
}

.guidance-card {
  display: flex;
  justify-content: space-between;
  gap: 2rem;
  background: linear-gradient(120deg, #f2f8f8, #fff 58%);
  border-left: 4px solid #168692;
}

.section-kicker {
  color: #168692;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.guidance-card h3,
.status-card h3,
.review-card h3 { margin: 0.25rem 0 0.4rem; }
.guidance-card p,
.status-heading p,
.review-heading p { margin: 0; color: #65747b; }
.guidance-detail { margin-top: 0.35rem !important; font-size: 0.88rem; }
code { color: #0d6670; background: #e5f2f2; padding: 0.08rem 0.3rem; border-radius: 4px; }

.limits {
  display: grid;
  grid-template-columns: repeat(3, minmax(95px, 1fr));
  align-content: center;
  gap: 0.5rem;
}

.limits span {
  display: flex;
  flex-direction: column;
  padding: 0.7rem;
  border-radius: 9px;
  background: #eef6f6;
  color: #607078;
  font-size: 0.75rem;
  text-align: center;
}

.limits strong { color: #123b4a; font-size: 1.1rem; }
.upload-summary { display: flex; align-items: center; justify-content: flex-end; gap: 1rem; margin-top: 1rem; color: #65747b; }

.status-heading,
.review-heading,
.progress-copy,
.status-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.status-pill,
.reason-pill {
  border-radius: 999px;
  padding: 0.35rem 0.7rem;
  font-size: 0.76rem;
  font-weight: 700;
  white-space: nowrap;
}

.status-pill.active { color: #765400; background: #fff1bf; }
.status-pill.success { color: #17623a; background: #dff4e8; }
.status-pill.danger,
.reason-pill.danger { color: #9d2626; background: #fde3e3; }
.reason-pill.warning { color: #765400; background: #fff1bf; }
.reason-pill.neutral { color: #46606a; background: #e8eef0; }

.phase-track { display: grid; grid-template-columns: repeat(5, 1fr); margin: 1.3rem 0; }
.phase-step { position: relative; display: flex; flex-direction: column; align-items: center; gap: 0.35rem; color: #8a979c; text-align: center; }
.phase-step::before { content: ""; position: absolute; top: 14px; right: 50%; width: 100%; height: 3px; background: #e0e6e8; z-index: 0; }
.phase-step:first-child::before { display: none; }
.phase-step > span { z-index: 1; display: grid; place-items: center; width: 30px; height: 30px; border-radius: 50%; background: #e0e6e8; font-size: 0.75rem; font-weight: 700; }
.phase-step.complete::before,
.phase-step.complete > span { background: #1c9a72; color: #fff; }
.phase-step.current > span { color: #fff; background: #168692; box-shadow: 0 0 0 5px #dceff0; }
.phase-step.current small { color: #125c64; font-weight: 700; }

.progress-panel { margin-bottom: 1rem; }
.progress-rail { height: 8px; overflow: hidden; border-radius: 10px; background: #e6ecee; margin-top: 0.45rem; }
.progress-rail span { display: block; height: 100%; border-radius: inherit; background: linear-gradient(90deg, #168692, #1c9a72); transition: width 0.2s ease; }

.count-grid { display: grid; grid-template-columns: repeat(7, minmax(90px, 1fr)); gap: 0.55rem; }
.count-card { display: flex; flex-direction: column; gap: 0.25rem; padding: 0.75rem; border-radius: 9px; background: #f3f6f7; border-bottom: 3px solid #aab6bb; }
.count-card span { color: #64737a; font-size: 0.72rem; }
.count-card strong { color: #263238; font-size: 1.3rem; }
.count-card.positive { border-color: #1c9a72; }
.count-card.warning { border-color: #d6a521; }
.count-card.danger { border-color: #d64949; }
.import-error { margin-top: 1rem; padding: 0.8rem; border-radius: 8px; color: #8e2929; background: #fff0f0; }
.status-actions { justify-content: flex-end; margin-top: 1rem; }

.review-list { display: grid; gap: 0.65rem; margin-top: 1rem; }
.review-item { display: grid; grid-template-columns: 72px minmax(0, 1fr) auto; gap: 0.85rem; align-items: center; padding: 0.75rem; border: 1px solid #e1e7e9; border-radius: 10px; background: #fafcfc; }
.item-preview { display: grid; place-items: center; width: 72px; height: 72px; overflow: hidden; border-radius: 8px; background: #e7edef; color: #809097; font-size: 1.5rem; }
.item-preview img { width: 100%; height: 100%; object-fit: cover; }
.item-main { min-width: 0; }
.item-main > small { display: block; color: #a13535; margin: 0.35rem 0; }
.item-title { display: flex; align-items: center; gap: 0.65rem; }
.item-title strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.key-editor { max-width: 430px; margin-top: 1.25rem; }
.key-editor .p-float-label,
.key-editor input { width: 100%; }
.item-actions { display: flex; gap: 0.4rem; }
.review-empty { display: flex; justify-content: center; align-items: center; gap: 0.5rem; min-height: 100px; color: #75848a; }
.review-empty i { color: #1c9a72; font-size: 1.3rem; }

@media (max-width: 1100px) {
  .guidance-card { flex-direction: column; }
  .limits { align-self: stretch; }
  .count-grid { grid-template-columns: repeat(4, 1fr); }
}

@media (max-width: 700px) {
  .bulk-image-manager { gap: 0.7rem; }
  .guidance-card,
  .upload-card,
  .status-card,
  .review-card { padding: 0.8rem; border-radius: 10px; }
  .mode-tab { flex-wrap: wrap; gap: 0.3rem; }
  .mode-tab small { width: 100%; }
  .limits { grid-template-columns: 1fr; }
  .limits span { flex-direction: row; justify-content: center; gap: 0.35rem; }
  .upload-summary,
  .status-heading,
  .review-heading { align-items: stretch; flex-direction: column; }
  .upload-summary .p-button { width: 100%; }
  .phase-step small { font-size: 0.62rem; }
  .phase-step > span { width: 25px; height: 25px; }
  .phase-step::before { top: 11px; }
  .count-grid { grid-template-columns: repeat(2, 1fr); }
  .review-item { grid-template-columns: 58px minmax(0, 1fr); align-items: start; }
  .item-preview { width: 58px; height: 58px; }
  .item-title { align-items: flex-start; flex-direction: column; gap: 0.25rem; }
  .item-actions { grid-column: 1 / -1; justify-content: flex-end; }
  .status-actions { flex-wrap: wrap; }
  .status-actions .p-button { flex: 1 1 auto; }
}
</style>
